import { useCallback } from "react";

import { instance } from "@/lib/axios/instance";

export interface CockpitPublicationItem {
  id: string;
  name: string | null;
}

export interface CockpitPublicationsPage {
  items: CockpitPublicationItem[];
  totalCount: number;
}

interface CockpitPublicationsApiResponse {
  data: CockpitPublicationItem[];
  page: number;
  pageSize: number;
  totalRecords: number;
}

const PAGE_SIZE = 20;

/**
 * Returns a paginated fetcher for Cockpit publications.
 * API returns standard shape: { data, page, pageSize, totalRecords }.
 * (filter?, page) => Promise<{ items, totalCount }> for use with AsyncSelect paginated mode.
 */
export function useCockpitPublicationFetcher(
  clientId: string | undefined,
  statusIds?: string[]
) {
  return useCallback(
    async (filter?: string, page = 1): Promise<CockpitPublicationsPage> => {
      if (!clientId) return { items: [], totalCount: 0 };
      const { data } = await instance.get<CockpitPublicationsApiResponse>(
        "/api/v2/Integrations/cockpit/job-source/Publications",
        {
          params: {
            clientId,
            filter: filter?.trim() || undefined,
            statusIds: statusIds?.length ? statusIds.join(",") : undefined,
            page,
            pageSize: PAGE_SIZE,
          },
        }
      );
      const items = data?.data ?? [];
      const totalCount = data?.totalRecords ?? items.length;
      return { items, totalCount };
    },
    [clientId, statusIds]
  );
}
